import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Slide } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import { MapInteractionCSS } from "react-map-interaction";
import { CircularProgress, Tooltip } from "@mui/material";
import GetAppOutlinedIcon from "@mui/icons-material/GetAppOutlined";

import * as S from "./styles";
import KOGImage from "../kogImage/kogImage";
import { OnKeyPressEnum } from "@/main/services/enum/OnKeyPressEnum";
import { getTranslateByKey } from "@/main/services/utils";

interface IValueScaleTranslation {
	scale: number;
	translation: {
		x: number;
		y: number;
	};
}

interface IKogSlideImages {
	images: string[];
	imageIndex: number;
	totalImages?: number;
	onLoadMore?: () => Promise<string[]>;
	downloadImage?: (url: string) => void;
}

function KogSlideImages({ images, imageIndex, totalImages, onLoadMore, downloadImage }: IKogSlideImages) {
	const [paginationIndexData, setPaginationIndexData] = useState(imageIndex);
	const [paginationIndex, setPaginationIndex] = useState(imageIndex + 1);
	const [extraImages, setExtraImages] = useState<string[]>([]);
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const allImages = useMemo(() => {
		const seen = new Set<string>();
		const result: string[] = [];
		for (const img of images) {
			if (!seen.has(img)) {
				seen.add(img);
				result.push(img);
			}
		}
		for (const img of extraImages) {
			if (!seen.has(img)) {
				seen.add(img);
				result.push(img);
			}
		}
		return result;
	}, [images, extraImages]);

	const displayTotal = totalImages ?? allImages.length;
	const hasMore = displayTotal > allImages.length && !!onLoadMore;

	const [slideStates, setSlideStates] = useState(
		Array.from({ length: allImages.length }, () => ({
			scale: 1,
			translation: { x: 0, y: 0 },
		})),
	);

	useEffect(() => {
		setSlideStates((prev) => {
			if (prev.length >= allImages.length) return prev;
			return [
				...prev,
				...Array.from({ length: allImages.length - prev.length }, () => ({
					scale: 1,
					translation: { x: 0, y: 0 },
				})),
			];
		});
	}, [allImages.length]);

	useEffect(() => {
		const prefetchThreshold = Math.max(0, allImages.length - 2);
		if (hasMore && !isLoadingMore && paginationIndexData >= prefetchThreshold) {
			setIsLoadingMore(true);
			onLoadMore!()
				.then((newImages) => {
					setExtraImages((prev) => [...prev, ...newImages]);
				})
				.finally(() => setIsLoadingMore(false));
		}
	}, [paginationIndexData, allImages.length, hasMore, isLoadingMore]);

	const handleSlideChange = useCallback(
		(oldIndex: number, newIndex: number) => {
			setPaginationIndex(newIndex + 1);
			setPaginationIndexData(newIndex);

			const oldSlideStates = [...slideStates];
			oldSlideStates[oldIndex] = {
				scale: 1,
				translation: { x: 0, y: 0 },
			};
			setSlideStates(oldSlideStates);
		},
		[slideStates],
	);

	function handleMapChange(index: number, value: IValueScaleTranslation) {
		const newSlideStates = [...slideStates];
		newSlideStates[index] = value;
		setSlideStates(newSlideStates);
	}

	const findItemOnScale = slideStates.find((item) => item.scale !== 1);

	const slideRef = useRef(null);
	const isAtLastLoadedSlide = paginationIndexData >= allImages.length - 1;

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === OnKeyPressEnum.ArrowLeft) {
				(slideRef.current as any)?.goBack();
			} else if (event.key === OnKeyPressEnum.ArrowRight) {
				if (isAtLastLoadedSlide && hasMore) return;
				(slideRef.current as any)?.goNext();
			}
		};

		document.addEventListener("keydown", handleKeyDown);

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleSlideChange, imageIndex, images.length, isAtLastLoadedSlide, hasMore]);

	return (
		<>
			<S.Pagination>
				{paginationIndex} / {displayTotal}
			</S.Pagination>
			{isLoadingMore && isAtLastLoadedSlide && (
				<S.LoadingOverlay>
					<CircularProgress size={48} style={{ color: "#fff" }} />
				</S.LoadingOverlay>
			)}
			<S.Container>
				<Slide
					key={allImages.length}
					ref={slideRef}
					arrows
					infinite={!hasMore && allImages.length > 1}
					autoplay={false}
					canSwipe={!findItemOnScale && !(isAtLastLoadedSlide && hasMore)}
					onChange={handleSlideChange}
					defaultIndex={paginationIndexData}
					duration={500}
					transitionDuration={500}
				>
					{allImages.map((image, index) => {
						return (
							<div key={index} className="each-slide">
								{downloadImage && (
									<Tooltip title={getTranslateByKey("information.downloadFile")}>
										<S.DownloadButton onClick={() => downloadImage(image)}>
											<GetAppOutlinedIcon />
										</S.DownloadButton>
									</Tooltip>
								)}

								<MapInteractionCSS
									showControls
									controlsClass="custom-map-controls"
									defaultValue={{
										scale: 1,
										translation: { x: 0, y: 0 },
									}}
									disablePan={(slideStates[index]?.scale ?? 1) === 1}
									onChange={({ scale, translation }: IValueScaleTranslation) => {
										handleMapChange(index, { scale, translation });
									}}
									value={{
										scale: slideStates[index]?.scale ?? 1,
										translation:
											(slideStates[index]?.scale ?? 1) === 1
												? { x: 0, y: 0 }
												: (slideStates[index]?.translation ?? { x: 0, y: 0 }),
									}}
									minScale={1}
									maxScale={2}
									translationBounds={{
										xMax: 400,
										yMax: 250,
										xMin: (slideStates[index]?.scale ?? 1) * -550,
										yMin: (slideStates[index]?.scale ?? 1) * -400,
									}}
								>
									<S.BoxImage>
										<S.ContainerImage>
											<KOGImage
												src={image}
												width="100%"
												style={{
													height: "100%",
												}}
											/>
										</S.ContainerImage>
									</S.BoxImage>
								</MapInteractionCSS>
							</div>
						);
					})}
				</Slide>
			</S.Container>
		</>
	);
}

export default KogSlideImages;
